import React, { useState } from 'react';
import { Clock, Trash2 } from 'lucide-react';
import { monthlyBenefits } from './constants';
import { formatShortDate } from './utils';
import type { MembershipPlan, Redemption } from './types';
import { DeleteRecordDialog } from './DeleteRecordDialog';

interface RedemptionHistoryProps {
  redemptions: Redemption[];
  plan: MembershipPlan;
  currentUserId?: string;
  isAdmin?: boolean;
  onDelete?: (redemptionId: string, reason: string) => Promise<void>;
}

export const RedemptionHistory: React.FC<RedemptionHistoryProps> = ({
  redemptions,
  plan,
  currentUserId,
  isAdmin = false,
  onDelete,
}) => {
  const benefits = monthlyBenefits[plan];
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRedemption, setSelectedRedemption] = useState<Redemption | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canDeleteRedemption = (redemption: Redemption) => {
    if (!onDelete) return false;
    if (isAdmin) return true;
    return redemption.redeemed_by === currentUserId;
  };

  const handleDeleteClick = (redemption: Redemption) => {
    setSelectedRedemption(redemption);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (reason: string) => {
    if (!selectedRedemption || !onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(selectedRedemption.id, reason);
      setDeleteDialogOpen(false);
      setSelectedRedemption(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-blue-500" />
          <span className="text-white font-medium">Historial de Canjes</span>
        </div>
        
        {redemptions.length === 0 ? (
          <p className="text-white/50 text-sm text-center py-4">
            Sin canjes registrados
          </p>
        ) : (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {redemptions.map((redemption) => (
              <div 
                key={redemption.id}
                className="flex items-center justify-between p-3 bg-white/5 rounded-xl group"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">
                    {benefits[redemption.benefit_type as keyof typeof benefits]?.name || redemption.benefit_type}
                  </p>
                  <p className="text-white/50 text-xs">
                    {formatShortDate(redemption.created_at)}
                  </p>
                  {redemption.notes && (
                    <p className="text-white/40 text-xs mt-1">{redemption.notes}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-blue-400 font-medium">x{redemption.quantity}</span>
                  {canDeleteRedemption(redemption) && (
                    <button
                      onClick={() => handleDeleteClick(redemption)}
                      className="p-1.5 rounded-lg bg-red-500/10 text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all"
                      title="Eliminar canje"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <DeleteRecordDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedRedemption(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Canje"
        description={selectedRedemption 
          ? `¿Eliminar el canje de "${benefits[selectedRedemption.benefit_type as keyof typeof benefits]?.name || selectedRedemption.benefit_type}"?`
          : ''
        }
        isDeleting={isDeleting}
      />
    </>
  );
};

export default RedemptionHistory;
